import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { toI18nextTemplate, type Language } from './cultures';

export interface InitI18nOptions<NS extends string> {
  /** The app's bundles per language and namespace, English source text as the key. */
  resources: Record<Language, Record<NS, Record<string, string>>>;
  /** One of the namespaces in `resources` (inferred from them, not from this). */
  defaultNS: NoInfer<NS>;
}

const convert = (bundle: Record<string, string>): Record<string, string> =>
  Object.fromEntries(Object.entries(bundle).map(([key, value]) => [key, toI18nextTemplate(value)]));

/**
 * Initialises the shared i18next instance with the conventions every Invite app follows: the
 * key is the English text, `{0}` placeholders as the SSR wrote them, and a missing key renders
 * verbatim like the SSR localizer.
 */
export const initI18n = <NS extends string>(opts: InitI18nOptions<NS>): void => {
  const resources = Object.fromEntries(
    Object.entries(opts.resources).map(([language, namespaces]) => [
      language,
      Object.fromEntries(
        Object.entries(namespaces as Record<string, Record<string, string>>).map(([ns, bundle]) => [
          ns,
          convert(bundle)
        ])
      )
    ])
  );
  void i18next.use(initReactI18next).init({
    resources,
    lng: 'en',
    fallbackLng: 'en',
    ns: Object.keys(opts.resources.en),
    defaultNS: opts.defaultNS,
    keySeparator: false,
    nsSeparator: false,
    interpolation: { escapeValue: false, prefix: '{{', suffix: '}}' },
    returnEmptyString: false,
    // Parity: the SSR localizer renders the key text verbatim when a key is missing.
    parseMissingKeyHandler: (key: string) => key
  });
};
